/**
 * TaskChangePhaseDialog - Modal "Alterar Fase" da tarefa
 *
 * Permite mover a tarefa para outra fase — inclusive de OUTRA categoria do
 * mesmo tipo/módulo da tarefa (normal | retention | legal). Usa a RPC
 * `move_task_phase` (v1.3.0), que já atualiza `category_id` conforme a fase
 * destino e valida `can_change_phase` server-side.
 *
 * Observação opcional: quando preenchida, é registrada como andamento
 * (comentário) na timeline da tarefa APÓS a mudança de fase.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-09 v1.4.0 - Permissões por fase passam a valer para TODAS
 *            as categorias (não só a atual): o seletor de destino desabilita
 *            fases sem can_change_phase também na troca de categoria.
 *   RELEASE: 2026-08-22 v1.3.0 - Layout padronizado com PremiumDialogHeader
 *            (mesmo padrão do modal "Nova anotação" do Chat).
 *   RELEASE: 2026-08-22 v1.2.0 - A observação deixa de virar um andamento
 *            separado: agora é enviada à RPC `move_task_phase` (p_note) e o
 *            trigger `trg_task_auto_comment` a inclui no log automático da
 *            mudança de fase (evita duplicidade na timeline).
 *   RELEASE: 2026-08-21 v1.1.0 - Observação deixa de repetir a transição de fase
 *            (já registrada pelo log automático do sistema) e passa a destacar
 *            o texto escrito pelo usuário em bloco de citação.
 *   RELEASE: 2026-08-21 v1.0.0 - Criação inicial
 */
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useTaskPhases } from '@/hooks/useTaskPhases';
import { useTaskPhasePermissions } from '@/hooks/useTaskPhasePermissions';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/components/ui/dialog';
import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, GitBranch } from 'lucide-react';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger do módulo (padrão de logging condicional do projeto)
const logger = createLogger('TaskChangePhaseDialog');

/** Limite de caracteres da observação (proteção contra payload abusivo) */
const MAX_NOTES = 1000;

interface TaskChangePhaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Tarefa alvo */
  taskId: string;
  /** Tipo/módulo da tarefa — escopa categorias e fases listadas */
  taskType: 'normal' | 'retention' | 'legal';
  /** Categoria atual */
  currentCategoryId?: string | null;
  /** Fase atual (exibição + validação) */
  currentPhaseId?: string | null;
  currentPhaseName?: string | null;
  currentPhaseColor?: string | null;
  /** Callback após sucesso (invalidações de cache ficam no pai) */
  onSuccess?: () => void;
  /**
   * @deprecated v1.2.0 — mantido apenas por compatibilidade de API. A
   * observação agora é gravada pela própria RPC/trigger, não mais como
   * andamento separado.
   */
  onAddNote?: (content: string, contentHtml?: string) => Promise<unknown>;
}

export function TaskChangePhaseDialog({
  open,
  onOpenChange,
  taskId,
  taskType,
  currentCategoryId,
  currentPhaseId,
  currentPhaseName,
  currentPhaseColor,
  onSuccess,
}: TaskChangePhaseDialogProps) {
  const { currentTenant } = useAuth();

  // Estado local do formulário
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [phaseId, setPhaseId] = useState<string | undefined>(undefined);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Categorias ativas do mesmo módulo da tarefa
  const { categories, isLoading: isLoadingCategories } = useTaskCategories(true, taskType);
  // Fases da categoria escolhida (escopadas ao módulo da tarefa)
  const { phases, isLoading: isLoadingPhases } = useTaskPhases(categoryId, taskType);
  // Permissões granulares por fase em TODAS as categorias do tenant —
  // necessário porque o diálogo permite trocar de categoria junto com a fase
  const { canDoOnPhase } = useTaskPhasePermissions();

  // Reset ao abrir: pré-seleciona a categoria atual
  useEffect(() => {
    if (open) {
      setCategoryId(currentCategoryId ?? undefined);
      setPhaseId(undefined);
      setNotes('');
    }
  }, [open, currentCategoryId]);

  // Fases disponíveis para destino (remove a fase atual)
  const availablePhases = useMemo(
    () => phases.filter((p) => p.id !== currentPhaseId),
    [phases, currentPhaseId],
  );

  const canSubmit = !!phaseId && phaseId !== currentPhaseId && !isSubmitting;

  /** Executa a mudança de fase (RPC) e, se houver, registra a observação */
  const handleSubmit = async () => {
    if (!phaseId) return;
    setIsSubmitting(true);
    try {
      const trimmed = notes.trim().slice(0, MAX_NOTES);

      // v1.2.0 — a observação vai junto na RPC; o trigger de log automático
      // complementa o andamento da mudança de fase com esse texto.
      const { error } = await supabase.rpc('move_task_phase', {
        p_task_id: taskId,
        p_new_phase_id: phaseId,
        p_note: trimmed || null,
        p_tenant_hint: currentTenant?.id || null,
      } as any);
      if (error) throw error;

      toast.success('Fase alterada com sucesso');
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro desconhecido';
      logger.error('Erro ao alterar fase da tarefa', message);
      toast.error(`Erro ao alterar fase: ${message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !isSubmitting && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Cabeçalho padrão do sistema (mesmo do modal "Nova anotação") */}
        <PremiumDialogHeader
          icon={GitBranch}
          title="Alterar Fase"
        />

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Fase atual */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Fase atual:</Label>
            <div className="flex items-center gap-2 text-sm">
              <span
                className="h-2.5 w-2.5 rounded-full shrink-0"
                style={{ backgroundColor: currentPhaseColor || 'hsl(var(--muted-foreground))' }}
              />
              <span className="font-medium">{currentPhaseName || 'Sem fase'}</span>
            </div>
          </div>

          {/* Categoria */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Categoria</Label>
            <Select
              value={categoryId}
              onValueChange={(v) => {
                setCategoryId(v);
                setPhaseId(undefined); // fase precisa ser reescolhida
              }}
              disabled={isLoadingCategories || isSubmitting}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Selecione a categoria" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: cat.color || 'hsl(var(--muted-foreground))' }}
                      />
                      <span>{cat.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Nova fase */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Nova Fase</Label>
            <Select
              value={phaseId}
              onValueChange={setPhaseId}
              disabled={!categoryId || isLoadingPhases || isSubmitting}
            >
              <SelectTrigger className="h-9">
                <SelectValue
                  placeholder={isLoadingPhases ? 'Carregando fases...' : 'Selecione a nova fase'}
                />
              </SelectTrigger>
              <SelectContent>
                {availablePhases.map((phase) => {
                  // Fase destino exige can_change_phase em QUALQUER categoria
                  // (permissões carregadas sem filtro de categoria)
                  const blocked = !canDoOnPhase(phase.id, 'can_change_phase');
                  return (
                    <SelectItem key={phase.id} value={phase.id} disabled={blocked}>
                      <span className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: phase.color || 'hsl(var(--muted-foreground))' }}
                        />
                        <span>{phase.name}</span>
                        {blocked && (
                          <span className="text-[10px] text-muted-foreground">(sem permissão)</span>
                        )}
                      </span>
                    </SelectItem>
                  );
                })}
                {availablePhases.length === 0 && !isLoadingPhases && (
                  <div className="px-2 py-3 text-xs text-muted-foreground">
                    Nenhuma outra fase disponível nesta categoria
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Observações */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Observações (opcional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, MAX_NOTES))}
              maxLength={MAX_NOTES}
              rows={3}
              placeholder="Digite observações sobre a alteração da fase..."
              disabled={isSubmitting}
            />
          </div>
        </div>

        <DialogFooter className="border-t bg-muted/30 px-6 py-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
            Alterar Fase
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
